import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Download, Package, MapPin, Calendar, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const customerAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('customer_token')}`,
});

export default function CustomerDocketDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [docket, setDocket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocket();
  }, [id]);

  const fetchDocket = async () => {
    try {
      const res = await fetch(`${API}/api/customer-portal/docket/${id}`, {
        headers: customerAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error('Failed to fetch docket');
      }
      const data = await res.json();
      setDocket(data);
    } catch (err) {
      console.error(err);
      alert('Unable to fetch docket details');
      navigate('/customer-portal/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    window.open(`/dockets/print/${id}`, '_blank');
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      PENDING: 'bg-yellow-500',
      IN_TRANSIT: 'bg-blue-500',
      DELIVERED: 'bg-green-500',
      COMPLETED: 'bg-green-600',
      CANCELLED: 'bg-red-500',
    };
    return (
      <Badge className={`${statusColors[status] || 'bg-gray-500'} text-white text-sm px-3 py-1`}>
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Package className="w-8 h-8 text-accent-blue animate-pulse" />
      </div>
    );
  }

  if (!docket) return null;

  const payload = docket.payload || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/customer-portal/dashboard')}
                className="h-8 w-8 p-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-text-dark font-manrope">
                  Docket Details
                </h1>
                <p className="text-xs text-text-secondary">{docket.system_docket_no}</p>
              </div>
            </div>
            <Button
              onClick={handleDownload}
              className="bg-accent-blue hover:bg-accent-blue/90"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Docket
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Status Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-secondary font-semibold uppercase tracking-wider mb-2">
                Shipment Status
              </p>
              {getStatusBadge(docket.status)}
            </div>
            <div className="text-right">
              <p className="text-sm text-text-secondary font-semibold uppercase tracking-wider mb-1">
                Docket Number
              </p>
              <p className="text-xl font-bold text-text-dark font-ibm">
                {docket.printed_docket_no || docket.system_docket_no}
              </p>
            </div>
          </div>
        </Card>

        {/* Shipment Details */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-text-dark font-manrope mb-5 flex items-center gap-2">
            <Package className="w-5 h-5 text-accent-blue" />
            Shipment Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                Shipment Type
              </p>
              <p className="text-base text-text-dark font-semibold">{docket.shipment_type}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                Docket Date
              </p>
              <p className="text-base text-text-dark">{formatDate(payload.docket_date)}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                No. of Packages
              </p>
              <p className="text-base text-text-dark">{payload.no_of_packages || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                Actual Weight
              </p>
              <p className="text-base text-text-dark">{payload.actual_weight || '-'} KG</p>
            </div>
          </div>
        </Card>

        {/* Addresses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Shipper */}
          <Card className="p-6">
            <h3 className="text-base font-semibold text-text-dark font-manrope mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-green-600" />
              Shipper (From)
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                  Name
                </p>
                <p className="text-sm text-text-dark">{payload.shipper_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                  Address
                </p>
                <p className="text-sm text-text-dark whitespace-pre-wrap">
                  {payload.shipper_address || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                  Contact
                </p>
                <p className="text-sm text-text-dark">{payload.shipper_contact || '-'}</p>
              </div>
            </div>
          </Card>

          {/* Consignee */}
          <Card className="p-6">
            <h3 className="text-base font-semibold text-text-dark font-manrope mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-accent-red" />
              Consignee (To)
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                  Name
                </p>
                <p className="text-sm text-text-dark">{payload.consignee_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                  Address
                </p>
                <p className="text-sm text-text-dark whitespace-pre-wrap">
                  {payload.consignee_address || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-secondary font-semibold uppercase tracking-wider mb-1">
                  Contact
                </p>
                <p className="text-sm text-text-dark">{payload.consignee_contact || '-'}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Description */}
        {payload.description_of_goods && (
          <Card className="p-6">
            <h3 className="text-base font-semibold text-text-dark font-manrope mb-3">
              Description of Goods
            </h3>
            <p className="text-sm text-text-dark whitespace-pre-wrap">
              {payload.description_of_goods}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
